import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Avatar,
  Chip,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Fade,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  LocalFireDepartment,
  ArrowForward,
  PlayCircleFilled,
  EmojiEvents,
  TipsAndUpdates,
  School,
  MilitaryTech,
  Info,
  Star,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Map,
  BarChart,
  AccessTime,
  BugReport,
  ThumbUp,
  LightbulbOutlined,
  ErrorOutline,
  HourglassEmpty,
  CheckCircleOutline,
  WorkspacePremium,
  Bolt,
  Psychology,
  Timeline,
  LiveTv as LiveIcon,
} from '@mui/icons-material';
import authService from '../services/authService';
import abacusService, { AbacusDashboardProgressDTO } from '../services/abacusService';
import { trackBadgeLabel } from '../utils/abacusCourseHome';
import {
  getAggregatedDashboard,
  getStudentDashboard,
  mapCourseDashboardToSummary,
  AggregatedDashboardDTO,
  CourseSummaryDTO,
} from '../services/studentDashboardService';
import { getActiveSessionInfo, ActiveSessionInfoDTO } from '../services/studentSessionService';
import { DisplayChip } from '../services/recommendationService';
import ActivityFeed from '../components/Activity/ActivityFeed';
import StudentRatingCard from '../components/Gamification/StudentRatingCard';
import BadgesDisplay from '../components/Gamification/BadgesDisplay';
import { useFlavorConfig } from '../hooks/useFlavorConfig';
import { FLAVOR_OPTIONS, Flavor } from '../config/themeConfig';
import CourseCard from '../components/dashboard/CourseCard';
import MissionCard from '../components/dashboard/MissionCard';
import StatTile from '../components/dashboard/StatTile';
import ScrollReveal from '../components/dashboard/ScrollReveal';

// ─────────────────────────────────────────────────────────────────────────────
// RANK SYSTEM
// ─────────────────────────────────────────────────────────────────────────────
const RANKS = [
  { name: 'Novice',              minPoints: 0,     tier: 0,  icon: '🌱' },
  { name: 'Apprentice',          minPoints: 2000,  tier: 1,  icon: '📖' },
  { name: 'Student',             minPoints: 4000,  tier: 2,  icon: '🎒' },
  { name: 'Scholar',             minPoints: 6000,  tier: 3,  icon: '🎓' },
  { name: 'Adept',               minPoints: 8000,  tier: 4,  icon: '⭐' },
  { name: 'Expert',              minPoints: 10000, tier: 5,  icon: '🔬' },
  { name: 'Master',              minPoints: 12000, tier: 6,  icon: '🏆' },
  { name: 'Grandmaster',         minPoints: 14000, tier: 7,  icon: '👑' },
  { name: 'Supreme Grandmaster', minPoints: 16000, tier: 8,  icon: '💎' },
  { name: 'Immortal Scholar',    minPoints: 18000, tier: 9,  icon: '🌟' },
  { name: 'Perfect Sage',        minPoints: 20000, tier: 10, icon: '✨' },
];

const POINT_RULES = [
  { action: 'Daily Login',        points: '+5',   icon: <LocalFireDepartment color="warning" /> },
  { action: 'Start a Topic',      points: '+10',  icon: <School color="primary" /> },
  { action: 'Complete a Topic',   points: '+50',  icon: <CheckCircleOutline color="success" /> },
  { action: 'Pass a Quiz',        points: '+50',  icon: <Star sx={{ color: '#FFD700' }} /> },
  { action: 'Score 100% on Quiz', points: '+100', icon: <WorkspacePremium sx={{ color: '#FFD700' }} /> },
  { action: 'Complete a Course',  points: '+500', icon: <EmojiEvents color="success" /> },
  { action: 'Earn a Badge',       points: '+25',  icon: <MilitaryTech color="primary" /> },
  { action: 'Report an Issue',    points: '+50',  icon: <BugReport color="error" /> },
  { action: 'Rate Topic Quality', points: '+20',  icon: <ThumbUp color="secondary" /> },
];

function rankFromPoints(points: number) {
  let idx = 0;
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (points >= RANKS[i].minPoints) { idx = i; break; }
  }
  return { rank: RANKS[idx], nextRank: RANKS[idx + 1] ?? null, idx };
}

/** Returns motivational copy based on current streak length. */
function streakCopy(days: number, name: string): string {
  if (days === 0) return `Ready to start your streak, ${name}? Log in tomorrow to begin!`;
  if (days === 1) return `Great start! Day 1 down — don't stop now.`;
  if (days < 7)  return `${days}-day streak! Keep the momentum going.`;
  if (days < 14) return `🔥 ${days} days and counting! You're on fire!`;
  if (days < 30) return `⚡ ${days}-day streak! Unstoppable force.`;
  return `🌟 ${days}-day LEGENDARY streak! True Immortal Scholar material.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { flavor, labels, setFlavor } = useFlavorConfig();

  const [openRules, setOpenRules] = useState(false);
  const [dashboard, setDashboard] = useState<AggregatedDashboardDTO | null>(null);
  const [homeCourses, setHomeCourses] = useState<CourseSummaryDTO[]>([]);
  const [coursesStatus, setCoursesStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loading, setLoading] = useState(true);
  const [activeSessions, setActiveSessions] = useState<Record<number, ActiveSessionInfoDTO>>({});

  // Animated points bar — starts at 0 and fills after data loads
  const [animatedPct, setAnimatedPct] = useState(0);

  // Carousel states
  const [missionIndex, setMissionIndex] = useState(0);
  const [statIndex, setStatIndex] = useState(0);
  const [abacusProgress, setAbacusProgress] = useState<AbacusDashboardProgressDTO | null>(null);

  const user = authService.getCurrentUser();
  const studentName = dashboard?.student?.name
    || (user ? `${user.firstName} ${user.lastName}` : 'Cadet');
  const firstName = studentName.split(' ')[0] || 'Cadet';

  const loadHomeCourses = () => {
    setCoursesStatus('loading');
    getStudentDashboard()
      .then(data => {
        const summaries = (data.courses ?? []).map(mapCourseDashboardToSummary);
        setHomeCourses(summaries);
        setCoursesStatus('ready');
        if (summaries.length > 0) {
          Promise.allSettled(summaries.map(course => getActiveSessionInfo(course.courseId).then(info => ({ id: course.courseId, info }))))
            .then(results => {
              const map: Record<number, ActiveSessionInfoDTO> = {};
              results.forEach(r => {
                if (r.status === 'fulfilled' && r.value.info) {
                  map[r.value.id] = r.value.info;
                }
              });
              setActiveSessions(map);
            });
        }
      })
      .catch(() => setCoursesStatus('error'));
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    setLoading(false);
    loadHomeCourses();
    getAggregatedDashboard().then(setDashboard).catch(() => undefined);
    abacusService.getDashboardProgress().then(setAbacusProgress).catch(() => setAbacusProgress(null));
  }, []);

  // ── Gamification values ──
  const gamif          = dashboard?.gamification;
  const totalPoints    = gamif?.currentPoints ?? 0;
  const streakDays     = gamif?.currentStreakDays ?? 0;
  const globalRank     = gamif?.globalRank ?? 0;
  const rankName       = gamif?.rankName ?? rankFromPoints(totalPoints).rank.name;
  const percentToNext  = gamif?.percentToNextRank ?? 0;
  const pointsToNext   = gamif?.pointsToNextRank ?? 0;
  const weeklyGoalPct  = gamif?.weeklyGoalPercent ?? 0;

  const localRankEntry = RANKS.find(r => r.name === rankName) ?? RANKS[0];

  // Animate the points bar after loading
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => setAnimatedPct(percentToNext), 400);
      return () => clearTimeout(t);
    }
  }, [loading, percentToNext]);

  // ── Stats ──
  const quizPassRate    = dashboard?.stats?.quizPassRate ?? 0;
  const topicsCompleted = dashboard?.stats?.totalTopicsCompleted ?? 0;
  const weeklyActivity  = dashboard?.stats?.weeklyActivityCount ?? 0;

  // ── Mission carousel ──
  const missions: Array<{
    title: string;
    desc: string;
    btnText: string;
    color: string;
    action: () => void;
  }> = [
    ...(dashboard?.topRecommendations?.slice(0, 3) ?? []).map(rec => ({
      title: rec.topicName,
      desc: rec.reason || `Continue your progress in <strong>${rec.courseName}</strong>`,
      btnText: `Start ${labels.missionLabel}`,
      color: '#00d2d3',
      action: () => navigate(`/student-courses/${rec.courseId}`),
    })),
    ...(homeCourses.slice(0, 2))
      .filter(c => c.currentTopic?.resumeReason === 'IN_PROGRESS')
      .map(c => ({
        title: c.currentTopic!.topicName,
        desc: `Resume your in-progress topic in <strong>${c.courseName}</strong>`,
        btnText: `Resume ${labels.missionLabel}`,
        color: '#a29bfe',
        action: () => navigate(`/student-courses/${c.courseId}`),
      })),
  ];

  if (missions.length === 0) {
    missions.push({
      title: `Your first ${labels.missionLabel}`,
      desc: `Enrol in a course to begin your first <strong>${labels.missionLabel}</strong>.`,
      btnText: 'Browse Courses',
      color: '#00d2d3',
      action: () => navigate('/student-courses'),
    });
  }

  const totalMissions  = missions.length;
  const safeIdx        = missionIndex % totalMissions;
  const currentMission = missions[safeIdx];

  // ── Stats widgets carousel (desktop sidebar) ──
  const STATS_WIDGETS = [
    {
      label: 'Weekly Goal',
      component: (
        <Box sx={{ position: 'relative', width: 110, height: 110 }}>
          <svg width="100%" height="100%" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
            <circle
              cx="50" cy="50" r="42" fill="none" stroke="#00d2d3" strokeWidth="8"
              strokeDasharray="264"
              strokeDashoffset={264 - (264 * weeklyGoalPct) / 100}
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 1.2s ease' }}
            />
          </svg>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" fontWeight="bold">{weeklyGoalPct}%</Typography>
            <Typography variant="caption" align="center" sx={{ opacity: 0.6, lineHeight: 1 }}>Weekly<br />Goal</Typography>
          </Box>
        </Box>
      ),
    },
    {
      label: 'Quiz Pass Rate',
      component: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 110 }}>
          <Star sx={{ fontSize: 44, color: '#fdcb6e', mb: 0.5 }} />
          <Typography variant="h5" fontWeight="bold">{quizPassRate}%</Typography>
          <Typography variant="caption" align="center" sx={{ opacity: 0.6 }}>Quiz Pass Rate</Typography>
        </Box>
      ),
    },
    {
      label: 'Topics Done',
      component: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 110 }}>
          <BarChart sx={{ fontSize: 44, color: '#ff7675', mb: 0.5 }} />
          <Typography variant="h5" fontWeight="bold">{topicsCompleted}</Typography>
          <Typography variant="caption" align="center" sx={{ opacity: 0.6 }}>Topics Completed</Typography>
        </Box>
      ),
    },
  ];
  const currentStat = STATS_WIDGETS[statIndex % STATS_WIDGETS.length];

  // ── Course navigation ──
  const handleContinueCourse = (course: CourseSummaryDTO) => {
    if (course.currentTopic) {
      navigate(`/student-courses/${course.courseId}/topics/${course.currentTopic.topicId}`);
    } else {
      navigate(`/student-courses/${course.courseId}`);
    }
  };

  // The "best" course to continue for the floating CTA
  const topCourse = homeCourses[0] ?? null;

  // ── Loading state ──
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#f5f6fa', minHeight: '100vh', pb: { xs: 12, md: 8 } }}>

      {/* ── STICKY MOBILE MINI-HEADER ── */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          background: 'linear-gradient(90deg, #1a1f2e 0%, #0d2137 100%)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 18 }}>{localRankEntry.icon}</Typography>
          <Box>
            <Typography variant="caption" sx={{ color: '#FFD700', fontWeight: 700, lineHeight: 1, display: 'block' }}>
              {rankName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>
              {totalPoints.toLocaleString()} pts
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {streakDays > 0 && (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.4,
                bgcolor: 'rgba(255,107,107,0.15)',
                borderRadius: 2, px: 1.2, py: 0.4,
              }}
            >
              <LocalFireDepartment
                sx={{
                  fontSize: 16, color: '#ff6b6b',
                  '@keyframes flicker': {
                    '0%, 100%': { transform: 'scale(1) rotate(-4deg)' },
                    '33%': { transform: 'scale(1.2) rotate(4deg)' },
                    '66%': { transform: 'scale(0.9) rotate(-2deg)' },
                  },
                  animation: 'flicker 1.3s ease-in-out infinite',
                }}
              />
              <Typography variant="caption" sx={{ color: '#ff6b6b', fontWeight: 700 }}>
                {streakDays}d
              </Typography>
            </Box>
          )}
          {globalRank > 0 && (
            <Chip
              label={`#${globalRank}`}
              size="small"
              sx={{ bgcolor: '#00d2d3', color: '#1a1f2e', fontWeight: 700, height: 22, fontSize: '0.65rem' }}
            />
          )}
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ pt: { xs: 1.5, md: 4 }, px: { xs: 1.5, md: 3 } }}>

        {/* ── LIVE SESSION BANNERS ── */}
        {Object.values(activeSessions).length > 0 && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {Object.values(activeSessions).map(session => {
              const course = homeCourses.find(c => c.courseId === session.courseId);
              const courseName = course?.courseName ?? `Course ${session.courseId}`;
              return (
                <Paper
                  key={session.courseId}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.2,
                    borderRadius: 2,
                    bgcolor: '#e8faf4',
                    border: '1px solid #00b894',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LiveIcon sx={{ color: '#00b894', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={600} color="#006c50">
                      Live session started in <strong>{courseName}</strong>
                      {session.tutorName ? ` by ${session.tutorName}` : ''}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate(`/courses/${session.courseId}/playground`)}
                    sx={{
                      bgcolor: '#00b894',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 2,
                      '&:hover': { bgcolor: '#00a381' },
                    }}
                  >
                    Open Playground
                  </Button>
                </Paper>
              );
            })}
          </Stack>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            SECTION 1 — HERO
        ══════════════════════════════════════════════════════════════════ */}
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: { xs: 3, md: 5 },
            background: 'linear-gradient(135deg, #0d1117 0%, #1a2a3a 50%, #0d2137 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
          }}
        >
          {/* Decorative watermark */}
          <MilitaryTech
            sx={{
              position: 'absolute', top: -40, right: -40,
              fontSize: { xs: 160, md: 320 },
              opacity: 0.04, transform: 'rotate(12deg)',
              pointerEvents: 'none',
            }}
          />

          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Grid container spacing={{ xs: 1.5, md: 3 }} alignItems="center">

              {/* COL 1: Rank badge + identity + points bar — hidden on mobile (mini-header covers this) */}
              <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                <Box
                  sx={{
                    pr: { md: 3 },
                    borderRight: { md: '1px solid rgba(255,255,255,0.08)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    {/* Glowing rank avatar */}
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        fontSize: 40,
                        border: '2px solid #FFD70088',
                        flexShrink: 0,
                        '@keyframes rankGlow': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(255,215,0,0.35), 0 0 16px rgba(255,215,0,0.2)' },
                          '50%': { boxShadow: '0 0 0 10px rgba(255,215,0,0), 0 0 28px rgba(255,215,0,0.4)' },
                        },
                        animation: 'rankGlow 2.5s ease-in-out infinite',
                      }}
                    >
                      {localRankEntry.icon}
                    </Avatar>

                    <Box>
                      <Typography
                        variant="overline"
                        sx={{ color: 'rgba(255,255,255,0.55)', letterSpacing: 1.5, fontSize: '0.65rem', lineHeight: 1 }}
                      >
                        {labels.heroGreeting.replace('Cadet', firstName)}
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight="900"
                        sx={{
                          color: '#FFD700',
                          textShadow: '0 2px 12px rgba(255,215,0,0.35)',
                          lineHeight: 1.1,
                          fontSize: '2rem',
                        }}
                      >
                        {rankName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                        {labels.rankSuffix}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Points bar */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        {totalPoints.toLocaleString()} pts
                      </Typography>
                      {pointsToNext > 0 && (
                        <Typography variant="body2" sx={{ color: '#00d2d3' }}>
                          {pointsToNext.toLocaleString()} to next
                        </Typography>
                      )}
                    </Box>
                    {/* Animated progress bar */}
                    <Box
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: `${animatedPct}%`,
                          borderRadius: 5,
                          background: 'linear-gradient(90deg, #00b894, #00d2d3)',
                          transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)',
                          boxShadow: '0 0 10px rgba(0,210,211,0.5)',
                        }}
                      />
                    </Box>
                  </Box>

                  <Button
                    startIcon={<Info />}
                    size="small"
                    onClick={() => setOpenRules(true)}
                    sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.75rem', p: 0 }}
                  >
                    Rank &amp; Point System
                  </Button>
                </Box>
              </Grid>

              {/* COL 2: Streak + Mission carousel */}
              <Grid item xs={12} md={5}>
                <Box sx={{ px: { md: 2 } }}>
                  {/* Streak + global rank row */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      {streakDays > 0 && (
                        <Box
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            bgcolor: 'rgba(255,107,107,0.12)',
                            borderRadius: 2, px: 1.5, py: 0.5,
                          }}
                        >
                          <LocalFireDepartment
                            sx={{
                              color: '#ff6b6b', fontSize: 20,
                              '@keyframes flicker': {
                                '0%, 100%': { transform: 'scale(1) rotate(-4deg)' },
                                '33%': { transform: 'scale(1.2) rotate(4deg)' },
                                '66%': { transform: 'scale(0.9) rotate(-2deg)' },
                              },
                              animation: 'flicker 1.3s ease-in-out infinite',
                            }}
                          />
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ff6b6b' }}>
                            {streakDays === 1 ? 'Day 1 Streak' : `${streakDays}-Day Streak`}
                          </Typography>
                        </Box>
                      )}
                      {globalRank > 0 && (
                        <Chip
                          size="small"
                          label={`#${globalRank} Global`}
                          icon={<TrendingUp sx={{ fontSize: '14px !important', color: '#1a1f2e !important' }} />}
                          sx={{ bgcolor: '#00d2d3', color: '#1a1f2e', fontWeight: 700, height: 24 }}
                        />
                      )}
                    </Box>

                    {/* Carousel prev/next */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setMissionIndex(p => (p - 1 + totalMissions) % totalMissions)}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setMissionIndex(p => (p + 1) % totalMissions)}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                      >
                        <ChevronRight />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Motivational copy */}
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', display: 'block', mb: 1 }}
                  >
                    {streakCopy(streakDays, firstName)}
                  </Typography>

                  {/* Mission card */}
                  <Fade key={safeIdx} in timeout={450}>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{ mb: 0.5, fontSize: { xs: '1rem', md: '1.3rem' }, lineHeight: 1.3 }}
                      >
                        {currentMission.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.75, mb: { xs: 1.5, md: 2.5 }, minHeight: { xs: 0, md: 38 }, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                        dangerouslySetInnerHTML={{ __html: currentMission.desc }}
                      />
                      <Stack direction="row" spacing={1.5}>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={currentMission.action}
                          startIcon={<PlayCircleFilled />}
                          sx={{
                            bgcolor: currentMission.color,
                            color: '#0d1117',
                            fontWeight: 700,
                            borderRadius: 2.5,
                            px: 2.5,
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'white', color: '#0d1117' },
                          }}
                        >
                          {currentMission.btnText}
                        </Button>
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<Map />}
                          onClick={() => navigate('/student-courses')}
                          sx={{
                            color: 'rgba(255,255,255,0.8)',
                            borderColor: 'rgba(255,255,255,0.2)',
                            borderRadius: 2.5,
                            textTransform: 'none',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
                          }}
                        >
                          {labels.coursesLabel}
                        </Button>
                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => navigate('/register-course-code')}
                          sx={{
                            color: 'rgba(255,255,255,0.8)',
                            borderColor: 'rgba(255,255,255,0.2)',
                            borderRadius: 2.5,
                            textTransform: 'none',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
                          }}
                        >
                          Join with a code
                        </Button>
                      </Stack>
                    </Box>
                  </Fade>
                </Box>
              </Grid>

              {/* COL 3: Stats carousel (desktop only) */}
              <Grid
                item
                xs={12}
                md={3}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderLeft: '1px solid rgba(255,255,255,0.08)',
                  pl: 3,
                }}
              >
                <Fade key={statIndex} in timeout={450}>
                  <Box sx={{ mb: 1.5 }}>{currentStat.component}</Box>
                </Fade>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => setStatIndex(p => (p - 1 + STATS_WIDGETS.length) % STATS_WIDGETS.length)}
                    sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.4)', minWidth: 72, textAlign: 'center' }}
                  >
                    {currentStat.label}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setStatIndex(p => (p + 1) % STATS_WIDGETS.length)}
                    sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* ══════════════════════════════════════════════════════════════════
            MAIN 2-COLUMN GRID: left (content) + right (stats/log/badges)
        ══════════════════════════════════════════════════════════════════ */}
        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="flex-start">

          {/* ── LEFT COLUMN ─────────────────────────────────────────────── */}
          <Grid item xs={12} md={7}>

            {abacusProgress && (
              <Paper
                data-testid="abacus-progress-widget"
                sx={{ p: 2, mb: 2 }}
                onClick={() => navigate(`/student-courses/${abacusProgress.courseId}/abacus`)}
              >
                <Typography variant="overline" color="text.secondary">Abacus</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {trackBadgeLabel(abacusProgress.track)} · {abacusProgress.levelTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {abacusProgress.topicsCompleted}/{abacusProgress.totalTopics} topics
                  {abacusProgress.nextTestWeekPending && abacusProgress.nextTestWeekTitle
                    ? ` · ${abacusProgress.nextTestWeekTitle} pending`
                    : ''}
                </Typography>
                {abacusProgress.levelBadges && abacusProgress.levelBadges.length > 0 && (
                  <Typography variant="body2" data-testid="abacus-level-badges">
                    {abacusProgress.levelBadges.join(' · ')}
                  </Typography>
                )}
              </Paper>
            )}

            {/* ── ACTIVE COURSES ── */}
            <ScrollReveal delay={0}>
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <School sx={{ color: '#6c5ce7', fontSize: 22 }} />
                    {labels.activeCoursesLabel}
                  </Typography>
                  <Button
                    endIcon={<ArrowForward />}
                    size="small"
                    color="inherit"
                    onClick={() => navigate('/student-courses')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    See All
                  </Button>
                </Box>

                {/* Horizontal scroll row on mobile, 2-col grid on sm+ */}
                {coursesStatus === 'loading' ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : coursesStatus === 'error' ? (
                  <Paper
                    elevation={0}
                    data-testid="courses-load-error"
                    sx={{
                      p: 4, textAlign: 'center',
                      borderRadius: 4, border: '2px dashed rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                    }}
                  >
                    <Typography color="text.secondary" gutterBottom>
                      Courses could not be loaded.
                    </Typography>
                    <Button
                      variant="contained"
                      data-testid="reload-courses"
                      sx={{ mt: 1 }}
                      onClick={loadHomeCourses}
                    >
                      Reload courses
                    </Button>
                  </Paper>
                ) : homeCourses.length > 0 ? (
                  <>
                    {/* Mobile: horizontal scroll */}
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        gap: 1.5,
                        overflowX: 'auto',
                        pb: 1,
                        mx: -1.5,
                        px: 1.5,
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                      }}
                    >
                      {homeCourses.map((course) => (
                        <Box
                          key={course.courseId}
                          sx={{ minWidth: '82vw', maxWidth: '82vw', scrollSnapAlign: 'start', flexShrink: 0 }}
                        >
                          <CourseCard
                            course={course}
                            continueLabel={labels.continueLabel}
                            onContinue={() => handleContinueCourse(course)}
                          />
                        </Box>
                      ))}
                    </Box>
                    {/* sm+: regular 2-col grid */}
                    <Grid container spacing={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                      {homeCourses.map((course) => (
                        <Grid item xs={12} sm={6} key={course.courseId}>
                          <CourseCard
                            course={course}
                            continueLabel={labels.continueLabel}
                            onContinue={() => handleContinueCourse(course)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4, textAlign: 'center',
                      borderRadius: 4, border: '2px dashed rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                    }}
                  >
                    <School sx={{ fontSize: 48, color: 'rgba(0,0,0,0.15)', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>
                      No active {labels.coursesLabel.toLowerCase()} yet.
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/student-courses')}
                    >
                      Browse {labels.coursesLabel}
                    </Button>
                  </Paper>
                )}
              </Box>
            </ScrollReveal>

            {/* ── MISSION BRIEFINGS (Recommendations) ── */}
            {(dashboard?.topRecommendations?.length ?? 0) > 0 && (
              <ScrollReveal delay={80}>
                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbOutlined sx={{ color: '#fdcb6e', fontSize: 22 }} />
                      Mission Briefings
                    </Typography>
                    <Button
                      endIcon={<ArrowForward />}
                      size="small"
                      color="inherit"
                      onClick={() => navigate('/student-courses')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Browse All
                    </Button>
                  </Box>
                  <Stack spacing={1.5}>
                    {dashboard!.topRecommendations.map(rec => (
                      <MissionCard
                        key={`${rec.courseId}-${rec.topicId}`}
                        rec={rec}
                        onClick={() => navigate(`/student-courses/${rec.courseId}`)}
                      />
                    ))}
                  </Stack>
                </Box>
              </ScrollReveal>
            )}

            {/* ── INTEL & INSIGHTS ── */}
            <ScrollReveal delay={120}>
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1.5, md: 2 } }}>
                  <TipsAndUpdates sx={{ color: '#FDCB6E', fontSize: 22 }} />
                  Intel &amp; Insights
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      tag: 'UPCOMING SKILL',
                      tagColor: '#00B894',
                      title: 'Data Analysis',
                      body: 'Currently learning Algebra II, which unlocks Data Science basics next semester.',
                      bg: '#E8F6F6',
                    },
                    {
                      tag: 'NEXT MILESTONE',
                      tagColor: '#FF7675',
                      title: 'Mid-Term Prep',
                      body: 'Completing 3 more modules ensures 100% coverage for the exam.',
                      bg: '#FFF0F0',
                    },
                    {
                      tag: 'CAREER PATH',
                      tagColor: '#6C5CE7',
                      title: 'Engineering',
                      body: 'Your Physics performance strongly aligns with Engineering tracks.',
                      bg: '#F0F3FF',
                    },
                  ].map((item) => (
                    <Grid item xs={12} sm={4} key={item.tag}>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, borderRadius: 3, bgcolor: item.bg, height: '100%' }}
                      >
                        <Typography
                          variant="overline"
                          sx={{ color: item.tagColor, fontWeight: 700, fontSize: '0.6rem', lineHeight: 1 }}
                        >
                          {item.tag}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 0.5, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                          {item.body}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </ScrollReveal>
          </Grid>

          {/* ── RIGHT COLUMN ────────────────────────────────────────────── */}
          <Grid item xs={12} md={5}>

            {/* ── STAT TILES ── */}
            <ScrollReveal delay={0}>
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1.5, md: 2 } }}>
                  <Timeline sx={{ color: '#00d2d3', fontSize: 22 }} />
                  Your Stats
                </Typography>
                {/* Mobile: 2×2 compact grid; desktop: vertical stack */}
                <Grid container spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                  {[
                    { icon: <TrendingUp sx={{ fontSize: 22 }} />, value: `${weeklyGoalPct}%`, label: 'Weekly Goal', color: '#00d2d3', bgColor: 'rgba(0,210,211,0.06)' },
                    { icon: <Star sx={{ fontSize: 22 }} />, value: `${quizPassRate}%`, label: 'Quiz Pass Rate', color: '#fdcb6e', bgColor: 'rgba(253,203,110,0.08)' },
                    { icon: <CheckCircleOutline sx={{ fontSize: 22 }} />, value: topicsCompleted, label: 'Topics Done', color: '#00b894', bgColor: 'rgba(0,184,148,0.06)' },
                    { icon: <Bolt sx={{ fontSize: 22 }} />, value: weeklyActivity, label: 'This Week', color: '#6c5ce7', bgColor: 'rgba(108,92,231,0.06)' },
                  ].map((s, i) => (
                    <Grid item xs={6} key={i}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 3,
                          bgcolor: s.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: `1px solid ${s.color}30`,
                        }}
                      >
                        <Box sx={{ color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 1.5, bgcolor: `${s.color}18`, flexShrink: 0 }}>
                          {s.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="900" sx={{ color: s.color, lineHeight: 1, fontSize: '1.1rem' }}>{s.value}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{s.label}</Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                {/* Desktop: full-size vertical stack */}
                <Stack spacing={1.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                  <StatTile
                    icon={<TrendingUp sx={{ fontSize: 26 }} />}
                    value={`${weeklyGoalPct}%`}
                    label="Weekly Goal Progress"
                    color="#00d2d3"
                    bgColor="rgba(0,210,211,0.06)"
                  />
                  <StatTile
                    icon={<Star sx={{ fontSize: 26 }} />}
                    value={`${quizPassRate}%`}
                    label="Quiz Pass Rate"
                    color="#fdcb6e"
                    bgColor="rgba(253,203,110,0.08)"
                  />
                  <StatTile
                    icon={<CheckCircleOutline sx={{ fontSize: 26 }} />}
                    value={topicsCompleted}
                    label="Topics Completed"
                    color="#00b894"
                    bgColor="rgba(0,184,148,0.06)"
                  />
                  <StatTile
                    icon={<Bolt sx={{ fontSize: 26 }} />}
                    value={weeklyActivity}
                    label="Activities This Week"
                    color="#6c5ce7"
                    bgColor="rgba(108,92,231,0.06)"
                  />
                </Stack>
              </Box>
            </ScrollReveal>

            {/* ── ACCOLADES & BADGES ── */}
            <ScrollReveal delay={60}>
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmojiEvents sx={{ color: '#FFD700', fontSize: 22 }} />
                    Accolades
                  </Typography>
                  <Button
                    endIcon={<ArrowForward />}
                    size="small"
                    color="inherit"
                    onClick={() => navigate('/student-accolades')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    See All
                  </Button>
                </Box>
                <Stack spacing={2}>
                  <BadgesDisplay />
                  <StudentRatingCard />
                </Stack>
              </Box>
            </ScrollReveal>

            {/* ── MISSION LOG (activity feed) ── */}
            <ScrollReveal delay={100}>
              <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid rgba(0,0,0,0.06)', bgcolor: 'white', overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 3, py: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime sx={{ color: '#57606f', fontSize: 22 }} />
                    {labels.activityFeedLabel}
                  </Typography>
                  <Button
                    endIcon={<ArrowForward />}
                    size="small"
                    color="inherit"
                    onClick={() => navigate('/student-activities')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Full History
                  </Button>
                </Box>

                <Box sx={{ px: 3, py: 2 }}>
                  {(dashboard?.recentActivity?.length ?? 0) > 0 ? (
                    <List disablePadding>
                      {dashboard!.recentActivity.map((item, i) => (
                        <ListItem
                          key={i}
                          disablePadding
                          sx={{
                            py: 1.5,
                            borderBottom: i < dashboard!.recentActivity.length - 1
                              ? '1px dashed rgba(0,0,0,0.07)'
                              : 'none',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#e1f5fe', color: '#03a9f4' }}>
                              <School sx={{ fontSize: 16 }} />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                                {item.description}
                              </Typography>
                            }
                            secondary={new Date(item.occurredAt).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}
                          />
                          {item.points != null && (
                            <Chip
                              label={`+${item.points} pts`}
                              size="small"
                              sx={{ bgcolor: '#e1f5fe', color: '#03a9f4', fontWeight: 700, height: 22, fontSize: '0.65rem' }}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <ActivityFeed />
                  )}
                </Box>
              </Paper>
            </ScrollReveal>
          </Grid>
        </Grid>

        {/* ══════════════════════════════════════════════════════════════════
            FLAVOR PICKER
        ══════════════════════════════════════════════════════════════════ */}
        <ScrollReveal delay={0}>
          <Paper
            elevation={0}
            sx={{
              mt: 3, p: 3, borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.06)',
              bgcolor: 'white',
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Dashboard Style
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose the theme that motivates you most.
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Dashboard Flavor</InputLabel>
              <Select
                value={flavor}
                label="Dashboard Flavor"
                onChange={(e) => setFlavor(e.target.value as Flavor)}
              >
                {FLAVOR_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        </ScrollReveal>
      </Container>

      {/* ══════════════════════════════════════════════════════════════════
          FLOATING "CONTINUE LEARNING" FAB — mobile only, fixed bottom-right
      ══════════════════════════════════════════════════════════════════ */}
      {topCourse && (
        <Tooltip title={`${labels.continueLabel} — ${topCourse.courseName}`} placement="left">
          <Fab
            variant="extended"
            onClick={() => handleContinueCourse(topCourse)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              bottom: 20,
              right: 16,
              zIndex: 1300,
              background: 'linear-gradient(135deg, #6c5ce7, #00d2d3)',
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.85rem',
              px: 2,
              boxShadow: '0 6px 20px rgba(108,92,231,0.45)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a4bd1, #00b8b8)',
                boxShadow: '0 8px 28px rgba(108,92,231,0.55)',
              },
            }}
          >
            <PlayCircleFilled sx={{ mr: 0.75, fontSize: 20 }} />
            {labels.continueLabel}
          </Fab>
        </Tooltip>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          RANK & POINT SYSTEM DIALOG
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog open={openRules} onClose={() => setOpenRules(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <MilitaryTech color="primary" /> Rank &amp; Point System
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                Ranking Structure
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Climb the ranks by earning points through learning activities. The rating system
                is similar to chess ELO — elite ranks represent genuine achievement.
              </Typography>
              <List dense>
                {RANKS.map((rank) => (
                  <ListItem key={rank.name} sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Typography
                          fontWeight={rank.name === rankName ? 'bold' : 'normal'}
                          color={rank.name === rankName ? 'primary' : 'inherit'}
                        >
                          {rank.icon} {rank.name}
                          {rank.name === rankName && (
                            <Chip label="Your rank" size="small" sx={{ ml: 1 }} color="primary" />
                          )}
                        </Typography>
                      }
                    />
                    <Chip label={`${rank.minPoints.toLocaleString()} pts`} size="small" variant="outlined" />
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid item xs={12} md={6} sx={{ borderLeft: { md: '1px solid rgba(0,0,0,0.08)' } }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom color="secondary">
                Point Rules
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Earn points through all learning activities. Streaks multiply your daily bonus.
              </Typography>
              <List>
                {POINT_RULES.map((rule) => (
                  <ListItem key={rule.action} sx={{ px: 0 }}>
                    <ListItemIcon>{rule.icon}</ListItemIcon>
                    <ListItemText
                      primary={rule.action}
                      secondary={
                        <Typography variant="caption" color="success.main" fontWeight="bold">
                          {rule.points} Points
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3, bgcolor: 'grey.50', p: 2.5, borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 2,
            }}
          >
            <Info color="action" />
            <Typography variant="body2">
              You are currently a <strong>{rankName}</strong> with {totalPoints.toLocaleString()} points.
              {pointsToNext > 0 && (
                <> You need <strong>{pointsToNext.toLocaleString()} more points</strong> to reach the next rank.</>
              )}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StudentDashboard;
